import React, { useEffect, useRef, useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import AppleSignin from 'react-apple-signin-auth';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { jwtDecode } from 'jwt-decode';
import { useAuth } from '../context/AuthContext';
import { FiArrowLeft, FiUsers, FiBriefcase, FiLoader, FiMapPin } from 'react-icons/fi';
import { MapContainer, Marker, Popup, TileLayer, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useLocation as useLocationHook } from './Tasks/hooks/useLocation';
import './Signup.css';

const STEPS = {
  AUTH: 'auth',
  CHOICE: 'choice',
  CREATE_COMPANY: 'create_company',
  JOIN_COMPANY: 'join_company',
};

const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  process.env.REACT_APP_GOOGLE_MAP_KEY ||
  '';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const COMPANY_MARKER_ICON = L.divIcon({
  className: 'company-location-marker',
  html: `
    <div style="
      width: 24px;
      height: 24px;
      border-radius: 50%;
      background: #111;
      border: 3px solid #fff;
      box-shadow: 0 8px 18px rgba(0,0,0,0.28), 0 0 0 4px rgba(17,17,17,0.14);
      position: relative;
    ">
      <div style="
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #fff;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      "></div>
    </div>
  `,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12],
});

const SignupMapClickHandler = ({ onPick }) => {
  useMapEvents({
    click: (e) => onPick({ lat: e.latlng.lat, lng: e.latlng.lng }),
  });
  return null;
};

const Signup = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [step, setStep] = useState(STEPS.AUTH);
  const [userDetails, setUserDetails] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Create Company fields
  const [companyName, setCompanyName] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [companyLocation, setCompanyLocation] = useState({
    address: '',
    coordinates: null,
  });
  const [mapKey, setMapKey] = useState(0);
  const [placeSuggestions, setPlaceSuggestions] = useState([]);
  const [showPlaceSuggestions, setShowPlaceSuggestions] = useState(false);
  const [isPlaceSearching, setIsPlaceSearching] = useState(false);
  const [mapsReady, setMapsReady] = useState(false);
  const suggestionsRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const geocoderRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const {
    isLoadingLocation,
    locationError,
    getCurrentLocationWithAddress,
    setLocationError,
  } = useLocationHook();

  // Join Company fields
  const [inviteCode, setInviteCode] = useState('');

  const handleAuthSuccess = async (email, firstName, lastName, picture, authProvider, authId) => {
    setError('');
    setIsLoading(true);

    const details = { email, firstName, lastName, picture, authProvider, authId };
    setUserDetails(details);

    try {
      const response = await api.post('/api/company/login', { email });
      login(response.data.token, response.data.user, response.data.company);
      navigate('/dashboard');
    } catch (err) {
      if (err.response?.status === 404 && err.response?.data?.message === 'Email not found.') {
        setCompanyName(`${firstName || ''}'s Company`.trim());
        setStep(STEPS.CHOICE);
      } else {
        setError(err.response?.data?.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = (credentialResponse) => {
    const decoded = jwtDecode(credentialResponse.credential);
    handleAuthSuccess(
      decoded.email,
      decoded.given_name || '',
      decoded.family_name || '',
      decoded.picture || '',
      'gmail',
      decoded.sub
    );
  };

  const handleAppleSuccess = (response) => {
    const email = response.authorization?.id_token
      ? jwtDecode(response.authorization.id_token).email
      : response.user?.email;

    if (!email) {
      setError('No email received from Apple Sign-In.');
      return;
    }

    const firstName = response.user?.name?.firstName || '';
    const lastName = response.user?.name?.lastName || '';

    handleAuthSuccess(email, firstName, lastName, '', 'apple', response.authorization?.code || '');
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    setError('');

    if (!companyName.trim()) {
      setError('Company name is required.');
      return;
    }
    if (!companyPhone.trim()) {
      setError('Phone number is required.');
      return;
    }
    if (!companyLocation.address.trim()) {
      setError('Company address is required.');
      return;
    }

    setIsLoading(true);
    try {
      let pendingPlan = null;
      try {
        const rawPlan = sessionStorage.getItem('pendingPlan');
        pendingPlan = rawPlan ? JSON.parse(rawPlan) : null;
      } catch (_) {
        pendingPlan = null;
      }

      const payload = {
        company: {
          name: companyName.trim(),
          email: userDetails.email,
          phone: companyPhone.trim(),
          website: companyWebsite.trim(),
          address: companyLocation.address.trim()
            ? {
                address: companyLocation.address.trim(),
                coords: companyLocation.coordinates || undefined,
              }
            : undefined,
        },
        admin: {
          firstName: userDetails.firstName,
          lastName: userDetails.lastName,
          email: userDetails.email,
          profilePicture: userDetails.picture,
          isAdmin: true,
          isActive: true,
          authProvider: userDetails.authProvider,
          authId: userDetails.authId,
        },
        plan: pendingPlan || undefined
      };

      const response = await api.post('/api/company/create', payload);
      if (response.data?.is_success) {
        sessionStorage.removeItem('pendingPlan');
        login(response.data.token, response.data.user, response.data.company);
        navigate('/onboarding');
      } else {
        setError(response.data?.message || 'Failed to create company.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create company. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const setupGooglePlaces = async () => {
      if (!GOOGLE_MAPS_API_KEY) return;
      const existing = document.getElementById('google-maps-places-script');
      if (!existing) {
        const script = document.createElement('script');
        script.id = 'google-maps-places-script';
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
        await new Promise((resolve, reject) => {
          script.onload = resolve;
          script.onerror = reject;
        });
      } else if (!(window.google && window.google.maps && window.google.maps.places)) {
        await new Promise((resolve) => {
          const wait = () => {
            if (window.google && window.google.maps && window.google.maps.places) return resolve();
            setTimeout(wait, 60);
          };
          wait();
        });
      }

      if (!cancelled && window.google?.maps?.places) {
        autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        placesServiceRef.current = new window.google.maps.places.PlacesService(document.createElement('div'));
        geocoderRef.current = new window.google.maps.Geocoder();
        setMapsReady(true);
      }
    };

    setupGooglePlaces().catch(() => {
      if (!cancelled) setLocationError('Google Places failed to load. Please try again.');
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowPlaceSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCompanyAddressChange = (value) => {
    setCompanyLocation((prev) => ({ ...prev, address: value }));
    if (value && locationError) setLocationError('');
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (!value || value.length < 3) {
      setPlaceSuggestions([]);
      setShowPlaceSuggestions(false);
      return;
    }
    if (!mapsReady || !autocompleteServiceRef.current) {
      setLocationError('Google Maps search is not ready yet.');
      return;
    }

    searchDebounceRef.current = setTimeout(() => {
      setIsPlaceSearching(true);
      autocompleteServiceRef.current.getPlacePredictions(
        {
          input: value,
          componentRestrictions: { country: 'in' },
          types: ['establishment'],
        },
        (predictions, status) => {
          const OK = window.google?.maps?.places?.PlacesServiceStatus?.OK;
          if (status === OK && Array.isArray(predictions) && predictions.length > 0) {
            setPlaceSuggestions(predictions);
            setShowPlaceSuggestions(true);
          } else {
            autocompleteServiceRef.current.getPlacePredictions(
              {
                input: value,
                componentRestrictions: { country: 'in' },
              },
              (fallbackPredictions, fallbackStatus) => {
                if (fallbackStatus === OK && Array.isArray(fallbackPredictions)) {
                  setPlaceSuggestions(fallbackPredictions);
                  setShowPlaceSuggestions(fallbackPredictions.length > 0);
                } else {
                  setPlaceSuggestions([]);
                  setShowPlaceSuggestions(false);
                }
              }
            );
          }
          setIsPlaceSearching(false);
        }
      );
    }, 300);
  };

  const handleSelectSuggestion = (suggestion) => {
    if (!placesServiceRef.current || !suggestion?.place_id) return;
    setIsPlaceSearching(true);
    placesServiceRef.current.getDetails(
      {
        placeId: suggestion.place_id,
        fields: [
          'name',
          'formatted_address',
          'geometry',
          'website',
          'formatted_phone_number',
          'international_phone_number',
        ],
      },
      (place, status) => {
        const OK = window.google?.maps?.places?.PlacesServiceStatus?.OK;
        if (status !== OK || !place) {
          setIsPlaceSearching(false);
          return;
        }

        const lat = place.geometry?.location?.lat?.();
        const lng = place.geometry?.location?.lng?.();
        setCompanyLocation({
          address: place.formatted_address || suggestion.description || '',
          coordinates: (lat != null && lng != null) ? { lat, lng } : null,
        });
        if ((!companyName || companyName.endsWith("'s Company")) && place.name) {
          setCompanyName(place.name);
        }
        if (!companyWebsite && place.website) {
          setCompanyWebsite(place.website);
        }
        if (!companyPhone && (place.formatted_phone_number || place.international_phone_number)) {
          const phone = (place.formatted_phone_number || place.international_phone_number || '').replace(/\D/g, '').slice(0, 15);
          setCompanyPhone(phone);
        }
        setShowPlaceSuggestions(false);
        setPlaceSuggestions([]);
        setMapKey((prev) => prev + 1);
        setIsPlaceSearching(false);
      }
    );
  };

  const handleGetCurrentLocation = async () => {
    const result = await getCurrentLocationWithAddress();
    if (result.success) {
      setCompanyLocation(result.location);
      if (result.warning) setLocationError(result.warning);
      setMapKey((prev) => prev + 1);
      return;
    }
    setLocationError(result.error || 'Unable to get your location.');
  };

  const handleMapPick = async ({ lat, lng }) => {
    setCompanyLocation((prev) => ({
      ...prev,
      coordinates: { lat, lng },
    }));
    if (geocoderRef.current) {
      geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
        const OK = window.google?.maps?.GeocoderStatus?.OK;
        const displayName = (status === OK && results?.[0]?.formatted_address) ? results[0].formatted_address : companyLocation.address;
        setCompanyLocation({
          address: displayName || '',
          coordinates: { lat, lng },
        });
      });
    }
  };

  const handleJoinCompany = async (e) => {
    e.preventDefault();
    setError('');

    if (!inviteCode.trim()) {
      setError('Please enter a company invite code.');
      return;
    }

    setIsLoading(true);
    try {
      let companyId, departmentId;
      try {
        const parsed = JSON.parse(atob(inviteCode.trim()));
        companyId = parsed.companyId;
        departmentId = parsed.departmentId;
      } catch {
        setError('Invalid invite code. Please check and try again.');
        setIsLoading(false);
        return;
      }

      const payload = {
        firstName: userDetails.firstName,
        lastName: userDetails.lastName,
        email: userDetails.email,
        profilePicture: userDetails.picture,
        authProvider: userDetails.authProvider,
        authId: userDetails.authId,
        companyId,
        departmentId,
        isSupervisor: true,
        isAdmin: false,
      };

      const response = await api.post('/api/users/create', payload);
      if (response.data?.is_success) {
        login(response.data.token, response.data.user, response.data.company);
        navigate('/dashboard');
      } else {
        setError(response.data?.message || 'Failed to join company.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to join company. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderAuthStep = () => (
    <div className="signup-box">
      <h1>Create your account</h1>
      <p>Get started with BloomNav in seconds</p>

      <div className="signup-buttons">
        <div className="google-login-button">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => setError('Google sign-in failed.')}
            theme="filled_black"
            text="signup_with"
            shape="rectangular"
          />
        </div>

        <div className="apple-login-button">
          <AppleSignin
            authOptions={{
              clientId: 'com.bloomnav.webapp',
              scope: 'name email',
              redirectURI: 'https://bloomnav.com/signup',
              state: 'signup',
              nonce: 'nonce',
              usePopup: true,
            }}
            onSuccess={handleAppleSuccess}
            onError={() => setError('Apple sign-in failed.')}
            skipScript={false}
            iconProp={{ style: { color: 'white' } }}
            textProp={{ color: 'white', fontSize: 12, fontWeight: 'bold' }}
            className="apple-auth-btn"
            buttonExtraChildren="Continue with Apple"
          />
        </div>
      </div>

      <div className="signup-footer-link">
        Already have an account? <Link to="/login">Sign in</Link>
      </div>
    </div>
  );

  const renderChoiceStep = () => (
    <div className="signup-box signup-box--wide">
      <button className="signup-back-btn" onClick={() => setStep(STEPS.AUTH)} type="button">
        <FiArrowLeft /> Back
      </button>
      <h1>Welcome, {userDetails?.firstName || 'there'}!</h1>
      <p>How would you like to get started?</p>

      <div className="signup-choice-cards">
        <button
          className="signup-choice-card"
          onClick={() => setStep(STEPS.CREATE_COMPANY)}
          type="button"
        >
          <div className="signup-choice-icon"><FiBriefcase /></div>
          <h3>Create a Company</h3>
          <span>Set up a new business on BloomNav</span>
        </button>

        <button
          className="signup-choice-card"
          onClick={() => setStep(STEPS.JOIN_COMPANY)}
          type="button"
        >
          <div className="signup-choice-icon"><FiUsers /></div>
          <h3>Join an Existing Company</h3>
          <span>Use an invite code from your team</span>
        </button>
      </div>
    </div>
  );

  const renderCreateCompanyStep = () => (
    <div className="signup-box">
      <button className="signup-back-btn" onClick={() => setStep(STEPS.CHOICE)} type="button">
        <FiArrowLeft /> Back
      </button>
      <h1>Create your company</h1>
      <p>Tell us about your business</p>

      <form className="signup-form" onSubmit={handleCreateCompany}>
        <div className="signup-field">
          <label htmlFor="companyAddress">Address *</label>
          <div className="signup-location-input-wrap" ref={suggestionsRef}>
            <input
              id="companyAddress"
              type="text"
              value={companyLocation.address}
              onChange={(e) => handleCompanyAddressChange(e.target.value)}
              placeholder="Search business address in India"
              required
            />
            {showPlaceSuggestions && placeSuggestions.length > 0 && (
              <div className="signup-location-suggestions">
                {placeSuggestions.map((suggestion, index) => (
                  <button
                    key={`${suggestion.place_id || index}`}
                    type="button"
                    className="signup-location-suggestion-item"
                    onClick={() => handleSelectSuggestion(suggestion)}
                  >
                    {suggestion.description}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            className="signup-location-btn"
            onClick={handleGetCurrentLocation}
            disabled={isLoadingLocation || isPlaceSearching}
          >
            {isLoadingLocation ? <FiLoader className="spin" /> : <FiMapPin />} Use current location
          </button>
          {isPlaceSearching && <div className="signup-location-hint">Searching places...</div>}
          {locationError && <div className="signup-location-error">{locationError}</div>}
          <div className="signup-map-wrap">
            {companyLocation?.coordinates?.lat && companyLocation?.coordinates?.lng ? (
              <MapContainer
                key={mapKey}
                center={[companyLocation.coordinates.lat, companyLocation.coordinates.lng]}
                zoom={15}
                scrollWheelZoom={false}
                style={{ height: 220, width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; OpenStreetMap contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <SignupMapClickHandler onPick={handleMapPick} />
                <Marker position={[companyLocation.coordinates.lat, companyLocation.coordinates.lng]} draggable eventHandlers={{
                  dragend: (e) => {
                    const pos = e.target.getLatLng();
                    handleMapPick({ lat: pos.lat, lng: pos.lng });
                  }
                }} icon={COMPANY_MARKER_ICON}>
                  <Popup>Company location</Popup>
                </Marker>
              </MapContainer>
            ) : (
              <MapContainer
                center={[20.5937, 78.9629]}
                zoom={4}
                scrollWheelZoom={false}
                style={{ height: 220, width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; OpenStreetMap contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <SignupMapClickHandler onPick={handleMapPick} />
              </MapContainer>
            )}
          </div>
        </div>

        <div className="signup-field">
          <label htmlFor="companyName">Company Name *</label>
          <input
            id="companyName"
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="e.g. Acme Corp"
            required
          />
        </div>

        <div className="signup-field">
          <label htmlFor="companyPhone">Phone Number *</label>
          <input
            id="companyPhone"
            type="tel"
            value={companyPhone}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, '');
              if (val.length <= 15) setCompanyPhone(val);
            }}
            placeholder="e.g. 9876543210"
            required
          />
        </div>

        <div className="signup-field">
          <label htmlFor="companyWebsite">Website <span className="optional">(optional)</span></label>
          <input
            id="companyWebsite"
            type="url"
            value={companyWebsite}
            onChange={(e) => setCompanyWebsite(e.target.value)}
            placeholder="e.g. https://acme.com"
          />
        </div>

        <button className="signup-submit-btn" type="submit" disabled={isLoading}>
          {isLoading ? <FiLoader className="spin" /> : 'Create Company'}
        </button>
      </form>
    </div>
  );

  const renderJoinCompanyStep = () => (
    <div className="signup-box">
      <button className="signup-back-btn" onClick={() => setStep(STEPS.CHOICE)} type="button">
        <FiArrowLeft /> Back
      </button>
      <h1>Join a company</h1>
      <p>Enter the invite code shared by your team admin</p>

      <form className="signup-form" onSubmit={handleJoinCompany}>
        <div className="signup-field">
          <label htmlFor="inviteCode">Invite Code *</label>
          <input
            id="inviteCode"
            type="text"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value)}
            placeholder="Paste invite code here"
            required
          />
        </div>

        <button className="signup-submit-btn" type="submit" disabled={isLoading}>
          {isLoading ? <FiLoader className="spin" /> : 'Join Company'}
        </button>
      </form>
    </div>
  );

  return (
    <div className="signup-container">
      {error && (
        <div className="signup-error" role="alert">
          {error}
          <button type="button" onClick={() => setError('')}>&times;</button>
        </div>
      )}

      {isLoading && step === STEPS.AUTH && (
        <div className="signup-box">
          <div className="signup-loading">
            <FiLoader className="spin" size={32} />
            <p>Checking your account...</p>
          </div>
        </div>
      )}

      {step === STEPS.AUTH && !isLoading && renderAuthStep()}
      {step === STEPS.CHOICE && renderChoiceStep()}
      {step === STEPS.CREATE_COMPANY && renderCreateCompanyStep()}
      {step === STEPS.JOIN_COMPANY && renderJoinCompanyStep()}
    </div>
  );
};

export default Signup;
